import { api, unwrap } from "@lumberset/api-client";
import {
  Button,
  Container,
  Field,
  Input,
  RadioGroup,
  SectionHeader,
  Switch,
  toast,
} from "@lumberset/ui";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { centsToDollars, dollarsToCents } from "../../lib/money";
import { settingsQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/settings")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(settingsQuery),
  component: Settings,
});

type ReviewMode = "gated" | "audit";

/** The settings, typed, with the values the platform assumes when a key was never saved. */
interface Values {
  "review.default_mode": ReviewMode;
  "review.force_gate_below_confidence": number;
  /** What a takeoff the workspace pays nothing for may spend on Reading, in cents. */
  "reading.trial_limit_cents": number;
  /** What a paid takeoff may spend on Reading, in cents; null for no limit. */
  "reading.paid_limit_cents": number | null;
  "reading.max_runs_per_workspace_per_day": number;
  "reading.max_free_runs_per_day": number;
  "signup.enabled": boolean;
  "pricebook.reviewed": boolean;
  "help.chat_daily_cost_cap_cents": number;
  "support.phone": string;
}

type Key = keyof Values;

function readValues(settings: Record<string, unknown>): Values {
  const mode = settings["review.default_mode"];
  const floor = settings["review.force_gate_below_confidence"];
  const trialLimit = settings["reading.trial_limit_cents"];
  const paidLimit = settings["reading.paid_limit_cents"];
  const perWorkspace = settings["reading.max_runs_per_workspace_per_day"];
  const freePerDay = settings["reading.max_free_runs_per_day"];
  const signup = settings["signup.enabled"];
  const pricebook = settings["pricebook.reviewed"];
  const chatCap = settings["help.chat_daily_cost_cap_cents"];
  const phone = settings["support.phone"];
  return {
    "review.default_mode": mode === "audit" ? "audit" : "gated",
    "review.force_gate_below_confidence": typeof floor === "number" ? floor : 0.5,
    "reading.trial_limit_cents": typeof trialLimit === "number" ? trialLimit : 3500,
    // Never saved, or saved empty: no limit.
    "reading.paid_limit_cents": typeof paidLimit === "number" ? paidLimit : null,
    "reading.max_runs_per_workspace_per_day": typeof perWorkspace === "number" ? perWorkspace : 10,
    "reading.max_free_runs_per_day": typeof freePerDay === "number" ? freePerDay : 30,
    "signup.enabled": typeof signup === "boolean" ? signup : true,
    "pricebook.reviewed": typeof pricebook === "boolean" ? pricebook : false,
    "help.chat_daily_cost_cap_cents": typeof chatCap === "number" ? chatCap : 500,
    "support.phone": typeof phone === "string" ? phone : "",
  };
}

/** What the Admin has typed, as text where a person edits text. */
interface Draft {
  mode: ReviewMode;
  floorPercent: string;
  trialLimitDollars: string;
  /** Empty for no limit. */
  paidLimitDollars: string;
  runsPerWorkspace: string;
  freeRunsPerDay: string;
  signup: boolean;
  pricebookReviewed: boolean;
  chatCapDollars: string;
  supportPhone: string;
}

const toDraft = (values: Values): Draft => ({
  mode: values["review.default_mode"],
  floorPercent: String(Math.round(values["review.force_gate_below_confidence"] * 100)),
  trialLimitDollars: centsToDollars(values["reading.trial_limit_cents"]),
  paidLimitDollars: centsToDollars(values["reading.paid_limit_cents"]),
  runsPerWorkspace: String(values["reading.max_runs_per_workspace_per_day"]),
  freeRunsPerDay: String(values["reading.max_free_runs_per_day"]),
  signup: values["signup.enabled"],
  pricebookReviewed: values["pricebook.reviewed"],
  chatCapDollars: centsToDollars(values["help.chat_daily_cost_cap_cents"]),
  supportPhone: values["support.phone"],
});

/** A whole number within bounds, or the current value when the text is not one. */
function wholeNumber(text: string, min: number, max: number, fallback: number): number {
  const value = Number(text);
  return Number.isInteger(value) ? Math.min(max, Math.max(min, value)) : fallback;
}

/** A paid takeoff's limit as typed, up to $1,000, or what is saved when the text is not dollars. */
function paidLimit(text: string, current: Values): number | null {
  const typed = dollarsToCents(text);
  return typed === null ? current["reading.paid_limit_cents"] : Math.min(100_000, typed);
}

function fromDraft(draft: Draft, current: Values): Values {
  const percent = Number(draft.floorPercent);
  const floor = Number.isFinite(percent)
    ? Math.min(1, Math.max(0, percent / 100))
    : current["review.force_gate_below_confidence"];
  return {
    "review.default_mode": draft.mode,
    "review.force_gate_below_confidence": Math.round(floor * 1000) / 1000,
    "reading.trial_limit_cents": Math.min(
      100_000,
      dollarsToCents(draft.trialLimitDollars) ?? current["reading.trial_limit_cents"],
    ),
    // Empty is no limit; text that is not dollars keeps what is saved.
    "reading.paid_limit_cents":
      draft.paidLimitDollars.trim() === "" ? null : paidLimit(draft.paidLimitDollars, current),
    "reading.max_runs_per_workspace_per_day": wholeNumber(
      draft.runsPerWorkspace,
      1,
      1000,
      current["reading.max_runs_per_workspace_per_day"],
    ),
    "reading.max_free_runs_per_day": wholeNumber(
      draft.freeRunsPerDay,
      0,
      10_000,
      current["reading.max_free_runs_per_day"],
    ),
    "signup.enabled": draft.signup,
    "pricebook.reviewed": draft.pricebookReviewed,
    "help.chat_daily_cost_cap_cents": Math.min(
      1_000_000,
      dollarsToCents(draft.chatCapDollars) ?? current["help.chat_daily_cost_cap_cents"],
    ),
    "support.phone": draft.supportPhone.trim(),
  };
}

function Settings() {
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(settingsQuery);
  const current = readValues(data.settings);
  const [draft, setDraft] = useState<Draft>(() => toDraft(current));

  const next = fromDraft(draft, current);
  const changed = (Object.keys(next) as Key[]).filter((key) => next[key] !== current[key]);

  const save = useMutation({
    mutationFn: (keys: Key[]) =>
      Promise.all(
        keys.map((key) =>
          unwrap(api.admin.settings[":key"].$put({ param: { key }, json: { value: next[key] } })),
        ),
      ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
      toast.success(saved.length === 1 ? "Setting saved" : `${saved.length} settings saved`);
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (changed.length > 0) save.mutate(changed);
  }

  return (
    <>
      <Crumbs trail={[{ label: "Settings" }]} />
      <form onSubmit={submit} className="flex flex-col gap-y-3">
        <Container className="divide-y p-0">
          <SectionHeader
            level="h1"
            title="Review"
            description="How Takeoffs reach workspaces. Changes take effect on the next Takeoff Run."
          />
          <div className="flex flex-col gap-y-5 px-6 py-5">
            <Field label="Review Mode for new workspaces">
              {(control) => (
                <RadioGroup
                  id={control.id}
                  aria-describedby={control["aria-describedby"]}
                  value={draft.mode}
                  onValueChange={(value) =>
                    setDraft((state) => ({ ...state, mode: value as ReviewMode }))
                  }
                  className="sm:grid-cols-2"
                >
                  <RadioGroup.ChoiceBox
                    value="gated"
                    label="Gated"
                    description="A Takeoff waits for a Reviewer before the workspace sees it."
                  />
                  <RadioGroup.ChoiceBox
                    value="audit"
                    label="Audit"
                    description="A Takeoff is released at once and a Reviewer checks it afterwards."
                  />
                </RadioGroup>
              )}
            </Field>

            <Field
              label="Always review below"
              hint="A Takeoff waits for Review even in audit mode when a line carrying at least 5% of its board feet, sheets, trusses, pieces or lineal feet has a Confidence under this. The only line of its kind, such as the truss line, carries all of it. 0% never forces a Review."
              className="sm:max-w-xs"
            >
              {(control) => (
                <Input
                  {...control}
                  type="number"
                  inputMode="numeric"
                  suffix="%"
                  min={0}
                  max={100}
                  step={1}
                  required
                  value={draft.floorPercent}
                  onChange={(event) =>
                    setDraft((state) => ({ ...state, floorPercent: event.target.value }))
                  }
                />
              )}
            </Field>
          </div>
        </Container>

        <Container className="divide-y p-0">
          <SectionHeader
            title="Reading"
            description="The AI step, and the only one that costs LumberSet money per run."
          />
          <div className="px-6 py-5">
            <p className="txt-compact-small text-pretty text-ui-fg-subtle">
              A reading limit is the most one takeoff may spend on model calls. A takeoff that
              reaches it stops reading and keeps what it read: it waits for a Reviewer to complete
              it by hand or read it again. A workspace can have its own limit, and an AI reading
              allowance that raises it, on its page. Workspaces never see either.
            </p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field
                label="Reading limit for a trial takeoff"
                hint="A takeoff the workspace pays nothing for: one of its trial takeoffs. Up to $1,000."
              >
                {(control) => (
                  <Input
                    {...control}
                    inputMode="decimal"
                    prefix="$"
                    required
                    pattern="[0-9]*\.?[0-9]{0,2}"
                    value={draft.trialLimitDollars}
                    onChange={(event) =>
                      setDraft((state) => ({ ...state, trialLimitDollars: event.target.value }))
                    }
                  />
                )}
              </Field>
              <Field
                label="Reading limit for a paid takeoff"
                optional
                hint="Paid by card, a takeoff credit, account credit, the month's included takeoffs or a Change Order Fee. Empty for no limit. Up to $1,000."
              >
                {(control) => (
                  <Input
                    {...control}
                    inputMode="decimal"
                    prefix="$"
                    placeholder="No limit"
                    pattern="[0-9]*\.?[0-9]{0,2}"
                    value={draft.paidLimitDollars}
                    onChange={(event) =>
                      setDraft((state) => ({ ...state, paidLimitDollars: event.target.value }))
                    }
                  />
                )}
              </Field>
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field
                label="Takeoffs one workspace may start in a day"
                hint="Stops a runaway account from reading drawings all day."
              >
                {(control) => (
                  <Input
                    {...control}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    value={draft.runsPerWorkspace}
                    onChange={(event) =>
                      setDraft((state) => ({ ...state, runsPerWorkspace: event.target.value }))
                    }
                  />
                )}
              </Field>
              <Field
                label="Free takeoffs per day, across everyone"
                hint="Runs by workspaces with no card on file and no account credit. Workspaces with a card or account credit are not counted."
              >
                {(control) => (
                  <Input
                    {...control}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    value={draft.freeRunsPerDay}
                    onChange={(event) =>
                      setDraft((state) => ({ ...state, freeRunsPerDay: event.target.value }))
                    }
                  />
                )}
              </Field>
            </div>
          </div>
        </Container>

        <Container className="divide-y p-0">
          <SectionHeader title="Sign up" />
          <div className="flex items-center justify-between gap-x-4 px-6 py-4">
            <label htmlFor="signup-enabled" className="min-w-0 cursor-pointer">
              <span className="txt-compact-small-plus block text-ui-fg-base">
                New workspaces can sign up
              </span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                Switch it off to stop new accounts and new workspaces while keeping every existing
                workspace working. People a workspace invites can still create their account to join
                it.
              </span>
            </label>
            <Switch
              id="signup-enabled"
              checked={draft.signup}
              onCheckedChange={(checked) => setDraft((state) => ({ ...state, signup: checked }))}
            />
          </div>
        </Container>

        <Container className="divide-y p-0">
          <SectionHeader
            title="Reference prices"
            description="The price book behind every Builder's Budget Estimate."
          />
          <div className="flex items-center justify-between gap-x-4 px-6 py-4">
            <label htmlFor="pricebook-reviewed" className="min-w-0 cursor-pointer">
              <span className="txt-compact-small-plus block text-ui-fg-base">
                The price book has been reviewed
              </span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                Until this is on, Budget Estimates say the reference prices are not yet reviewed.
              </span>
            </label>
            <Switch
              id="pricebook-reviewed"
              checked={draft.pricebookReviewed}
              onCheckedChange={(checked) =>
                setDraft((state) => ({ ...state, pricebookReviewed: checked }))
              }
            />
          </div>
        </Container>

        <Container className="divide-y p-0">
          <SectionHeader
            title="Help Center"
            description="The help assistant answers from the help articles with a model call per question."
          />
          <div className="px-6 py-5">
            <Field
              label="Most the assistant may spend in a day"
              hint="Across every workspace. When it is reached, the assistant offers a ticket instead until tomorrow. Up to $10,000."
              className="sm:max-w-xs"
            >
              {(control) => (
                <Input
                  {...control}
                  inputMode="decimal"
                  prefix="$"
                  required
                  pattern="[0-9]*\.?[0-9]{0,2}"
                  value={draft.chatCapDollars}
                  onChange={(event) =>
                    setDraft((state) => ({ ...state, chatCapDollars: event.target.value }))
                  }
                />
              )}
            </Field>
          </div>
        </Container>

        <Container className="divide-y p-0">
          <SectionHeader
            title="Support"
            description="How people reach a person from the Help Center's Contact tab."
          />
          <div className="px-6 py-5">
            <Field
              label="Phone number"
              optional
              hint="Shown as a way to call Support. Leave it empty to offer no phone line."
              className="sm:max-w-xs"
            >
              {(control) => (
                <Input
                  {...control}
                  type="tel"
                  maxLength={40}
                  placeholder="(555) 010-2030"
                  value={draft.supportPhone}
                  onChange={(event) =>
                    setDraft((state) => ({ ...state, supportPhone: event.target.value }))
                  }
                />
              )}
            </Field>
          </div>
        </Container>

        <div className="flex items-center justify-end gap-x-3">
          {changed.length > 0 ? (
            <span className="txt-compact-small text-ui-fg-muted">
              {changed.length === 1 ? "1 change" : `${changed.length} changes`}
            </span>
          ) : null}
          <Button type="submit" disabled={changed.length === 0} isLoading={save.isPending}>
            Save changes
          </Button>
        </div>
      </form>
    </>
  );
}
